package com.partejar.app.core.ui.components

import androidx.compose.foundation.layout.*
import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.verticalScroll
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.text.KeyboardOptions
import androidx.compose.animation.core.MutableTransitionState
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.runtime.saveable.rememberSaveable
import androidx.compose.ui.Modifier
import androidx.compose.ui.Alignment
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.LiveRegionMode
import androidx.compose.ui.semantics.liveRegion
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.AnnotatedString
import androidx.compose.ui.text.input.*
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import com.partejar.app.core.format.*
import com.partejar.app.core.ui.theme.PartejarIcons
import com.partejar.app.core.ui.theme.PartejarTheme
import kotlinx.datetime.*
import kotlin.time.Instant
import kotlin.time.Clock

/** Raw digits under a visual mask preserve cursor/deletion behavior, including around slashes. */
internal fun brazilianDateInput(digits: String): String = buildString {
    digits.take(8).forEachIndexed { index, char ->
        if (index == 2 || index == 4) append('/')
        append(char)
    }
}

internal val BrazilianDateMask = VisualTransformation { text ->
    val formatted = brazilianDateInput(text.text)
    TransformedText(AnnotatedString(formatted), object : OffsetMapping {
        override fun originalToTransformed(offset: Int): Int =
            (offset + (if (offset > 2) 1 else 0) + (if (offset > 4) 1 else 0)).coerceAtMost(formatted.length)
        override fun transformedToOriginal(offset: Int): Int =
            (offset - (if (offset > 2) 1 else 0) - (if (offset > 5) 1 else 0)).coerceIn(0, text.length)
    })
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun PartejarDateField(
    value: String,
    onValueChange: (String) -> Unit,
    label: String,
    enabled: Boolean = true,
    error: String? = null,
    preferCompactPicker: Boolean = false,
    modifier: Modifier = Modifier // 1. Modificador exposto para o mundo exterior
) {
    var pickerOpen by rememberSaveable { mutableStateOf(false) }

    // 2. Encapsulamento de Layout: Garante que o campo e o erro viajem sempre juntos
    Column(modifier = modifier) {
        PartejarTextField(
            value = value.filter { it in '0'..'9' },
            onValueChange = { input ->
                if (input.all { it in '0'..'9' || it == '/' } && input.count { it in '0'..'9' } <= 8) {
                    onValueChange(brazilianDateInput(input.filter { it in '0'..'9' }))
                }
            },
            label = label,
            modifier = Modifier.fillMaxWidth(), // Ocupa todo o espaço que a Column permitir
            enabled = enabled,
            isError = error != null,
            placeholder = "dd/MM/aaaa",
            visualTransformation = BrazilianDateMask,
            keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number),
            trailingIcon = {
                PartejarIconButton(onClick = { pickerOpen = true }, enabled = enabled) {
                    Icon(PartejarIcons.Clinical.calendar, "Selecionar data no calendário")
                }
            }
        )

        error?.let {
            Text(
                text = it,
                color = MaterialTheme.colorScheme.error,
                style = MaterialTheme.typography.bodySmall,
                modifier = Modifier
                    .padding(top = 4.dp, start = 16.dp) // Alinhamento premium europeu com a borda do input
                    .semantics { liveRegion = LiveRegionMode.Polite }
            )
        }
    }

    // O modal do calendário continua aqui para baixo sem alterações...
    if (pickerOpen && enabled) {
        val visibility = remember { MutableTransitionState(false).apply { targetState = true } }
        var confirmed by remember { mutableStateOf<String?>(null) }
        LaunchedEffect(visibility.isIdle, visibility.currentState) {
            if (visibility.isIdle && !visibility.currentState && !visibility.targetState) {
                confirmed?.let(onValueChange)
                pickerOpen = false
            }
        }
        val dismiss = { visibility.targetState = false }
        val selectedYear = parseBrazilianDate(value)?.year
        val dateState = rememberDatePickerState(
            initialSelectedDateMillis = brazilianDateToDatePickerMillis(value),
            initialDisplayedMonthMillis = brazilianDateToDatePickerMillis(value)
                ?: Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds(),
            yearRange = minOf(1900, selectedYear ?: 1900)..maxOf(2100, selectedYear ?: 2100)
        )
        val spacing = PartejarTheme.spacing
        val calendarWidth = spacing.space48 * 7 + spacing.space24

        Dialog(onDismissRequest = dismiss, properties = DialogProperties(usePlatformDefaultWidth = false)) {
            PartejarVisibility(visibility) {
                Surface(
                    modifier = Modifier.windowInsetsPadding(WindowInsets.safeDrawing)
                        .widthIn(max = calendarWidth).fillMaxWidth(),
                    shape = MaterialTheme.shapes.extraLarge,
                    color = MaterialTheme.colorScheme.surfaceContainerLow
                ) {
                    Column {
                        Column(Modifier.padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                            Text(label, style = MaterialTheme.typography.labelLarge)
                            Text(dateState.selectedDateMillis?.let(::datePickerMillisToBrazilianDate) ?: "Selecione uma data",
                                style = MaterialTheme.typography.headlineSmall)
                        }
                        Column(Modifier.weight(1f, fill = false).verticalScroll(rememberScrollState())) {
                            BoxWithConstraints(Modifier.fillMaxWidth()) {
                                if (preferCompactPicker || maxWidth < calendarWidth) CompactDateCalendar(dateState)
                                else DatePicker(state = dateState, modifier = Modifier.fillMaxWidth(), showModeToggle = false, title = null, headline = null)
                            }
                        }
                        Column(Modifier.padding(spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                            PartejarButton("Confirmar data", onClick = {
                                confirmed = dateState.selectedDateMillis?.let(::datePickerMillisToBrazilianDate)
                                dismiss()
                            }, enabled = dateState.selectedDateMillis != null && visibility.targetState, motionEnabled = true,
                                leadingIcon = { Icon(PartejarIcons.Feedback.success, null) })
                            PartejarButton("Cancelar", onClick = dismiss, enabled = visibility.targetState, variant = PartejarButtonVariant.TERTIARY,
                                motionEnabled = true, leadingIcon = { Icon(PartejarIcons.Actions.back, null) })
                        }
                    }
                }
            }
        }
    }
}

/** Adaptive rendering of DatePickerState; month arithmetic remains in the existing datetime library. */
@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun CompactDateCalendar(state: DatePickerState) {
    val spacing = PartejarTheme.spacing
    val dimensions = PartejarTheme.dimensions
    val month = Instant.fromEpochMilliseconds(state.displayedMonthMillis).toLocalDateTime(TimeZone.UTC).date
    val cells = remember(month) { calendarMonthCells(month) }
    val selectedDate = state.selectedDateMillis?.let { Instant.fromEpochMilliseconds(it).toLocalDateTime(TimeZone.UTC).date }
    val today = Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date
    var changingYear by remember { mutableStateOf(false) }
    var yearInput by remember(month.year) { mutableStateOf(month.year.toString()) }
    val monthNames = listOf("Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho", "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro")
    fun moveMonth(delta: Int) {
        val target = month.plus(DatePeriod(months = delta))
        if (target.year in state.yearRange) state.displayedMonthMillis = target.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
    }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
        Row(Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically) {
            PartejarIconButton(onClick = { moveMonth(-1) }, enabled = month.year > state.yearRange.first || month.monthNumber > 1) {
                Icon(PartejarIcons.Actions.back, "Mês anterior")
            }
            TextButton(onClick = { changingYear = !changingYear }, modifier = Modifier.weight(1f)) {
                Text("${monthNames[month.monthNumber - 1]} ${month.year}")
            }
            PartejarIconButton(onClick = { moveMonth(1) }, enabled = month.year < state.yearRange.last || month.monthNumber < 12) {
                Icon(PartejarIcons.Actions.next, "Próximo mês")
            }
        }
        if (changingYear) {
            Column(Modifier.padding(horizontal = spacing.space16), verticalArrangement = Arrangement.spacedBy(spacing.space8)) {
                PartejarTextField(yearInput, { if (it.length <= 4 && it.all(Char::isDigit)) yearInput = it }, label = "Ano",
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Number))
                PartejarButton("Selecionar ano", onClick = {
                    yearInput.toIntOrNull()?.takeIf { it in state.yearRange }?.let { year ->
                        state.displayedMonthMillis = LocalDate(year, month.monthNumber, 1).atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds()
                        changingYear = false
                    }
                }, enabled = yearInput.toIntOrNull()?.let { it in state.yearRange } == true, motionEnabled = true,
                    leadingIcon = { Icon(PartejarIcons.Feedback.success, null) })
            }
        }
        Row(Modifier.fillMaxWidth()) {
            listOf("D", "S", "T", "Q", "Q", "S", "S").forEach { day ->
                Box(Modifier.weight(1f).heightIn(min = dimensions.minimumTouchTarget), contentAlignment = Alignment.Center) {
                    Text(day, style = MaterialTheme.typography.labelMedium, color = MaterialTheme.colorScheme.onSurfaceVariant)
                }
            }
        }
        cells.chunked(7).forEach { week ->
            Row(Modifier.fillMaxWidth()) {
                week.forEach { day ->
                    Box(Modifier.weight(1f).heightIn(min = dimensions.minimumTouchTarget), contentAlignment = Alignment.Center) {
                        if (day != null) {
                            val selected = day == selectedDate
                            val isToday = day == today
                            val dateText = requireNotNull(formatBrazilianDate(day.toString()))
                            TextButton(onClick = { state.selectedDateMillis = day.atStartOfDayIn(TimeZone.UTC).toEpochMilliseconds() },
                                modifier = Modifier.fillMaxWidth().heightIn(min = dimensions.minimumTouchTarget).semantics {
                                    contentDescription = if (isToday) "$dateText, hoje" else dateText
                                    this.selected = selected
                                },
                                contentPadding = PaddingValues(spacing.space4),
                                border = if (isToday) BorderStroke(dimensions.strokeThin, MaterialTheme.colorScheme.primary) else null,
                                colors = ButtonDefaults.textButtonColors(
                                    containerColor = if (selected) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.surfaceContainerLow,
                                    contentColor = if (selected) MaterialTheme.colorScheme.onPrimary else MaterialTheme.colorScheme.onSurface
                                )) { Text(day.dayOfMonth.toString()) }
                        }
                    }
                }
            }
        }
    }
}
